import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import { useState } from 'react';
import { Button } from '../../../../../../../components/Button';
import { Checkbox } from '../../../../../../../components/Checkbox';
import { ContactEmailUnsubscribeInfo } from '../../../../../../../components/contacts/ContactEmailUnsubscribeInfo';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { SimpleTooltip, TooltipProvider } from '../../../../../../../components/Tooltip';
import { styleSheet } from '../../styles';
import { EmailCard } from './EmailCard';

const localStyles = StyleSheet.create({
	tooltipContent: {
		maxWidth: '250px',
	},
});

type Props = {
	contact: Api.IContact;
	contactVm: Api.ContactViewModel;
	onUnsubscribeFromEmailCheckboxChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onEmailChanged: (emails: Api.EmailAddress[]) => void;
	isUnsubscribing: boolean;
	disableUnsubscribeCheckbox: boolean;
};

export const EmailList = ({
	contact,
	contactVm,
	onEmailChanged,
	onUnsubscribeFromEmailCheckboxChanged,
	isUnsubscribing,
	disableUnsubscribeCheckbox,
}: Props) => {
	const [isAdding, setIsAdding] = useState(false);
	const [emailValue, setEmailValue] = useState('');

	const onEmailUpdated = (email: Api.EmailAddress, index: number) => {
		const newEmails = [...(contact.emailAddresses || [])];
		if (index < 0 || index >= newEmails.length) {
			return;
		}

		newEmails[index] = email;
		onEmailChanged(newEmails);
	};

	const onSaveNewEmail = () => {
		onEmailChanged([...(contact.emailAddresses || []), { value: emailValue }]);
		setIsAdding(false);
		setEmailValue('');
	};

	const onMakePrimaryClicked = (index: number) => {
		onEmailChanged([contact.emailAddresses[index], ...(contact.emailAddresses || []).filter((_, i) => i !== index)]);
	};

	const onDeleteEmail = (index: number) => {
		onEmailChanged([...(contact.emailAddresses || []).filter((_, i) => i !== index)]);
	};

	return (
		<div className={css(styleSheet.contactDetailSection)}>
			<TooltipProvider>
				{disableUnsubscribeCheckbox ? (
					<SimpleTooltip
						title='Unsubscribe status is managed via an integration. To re-subscribe this contact, make the change in your integrated system and it will propagate to Levitate.'
						styles={[localStyles.tooltipContent]}
					>
						<div>
							<Checkbox
								disabled={true}
								checked={!!(contactVm.tags || []).find(x => !!x && x.toLocaleLowerCase() === 'unsubscribe')}
								className={css(styleSheet.contactDetailUnsubscribeCheckbox)}
								id='email-unsubscribe-checkbox'
								onChange={onUnsubscribeFromEmailCheckboxChanged}
							>
								<span>Unsubscribe this contact from my group emails</span>
							</Checkbox>
						</div>
					</SimpleTooltip>
				) : (
					<Checkbox
						disabled={isUnsubscribing}
						checked={!!(contactVm.tags || []).find(x => !!x && x.toLocaleLowerCase() === 'unsubscribe')}
						className={css(styleSheet.contactDetailUnsubscribeCheckbox)}
						id='email-unsubscribe-checkbox'
						onChange={onUnsubscribeFromEmailCheckboxChanged}
					>
						<span>Unsubscribe this contact from my group emails</span>
					</Checkbox>
				)}
			</TooltipProvider>

			<ContactEmailUnsubscribeInfo contact={contactVm} className={css(styleSheet.contactDetailUnsubscribeInfo)} />

			<div className={css(styleSheet.emailCardContainer)}>
				{contact.emailAddresses?.map((email, index) => (
					<EmailCard
						email={email}
						key={`${email.value}-${index}`}
						contact={contactVm}
						onEmailChanged={(newEmail: Api.EmailAddress) => onEmailUpdated(newEmail, index)}
						isPrimary={index === 0}
						onMakePrimaryClicked={() => onMakePrimaryClicked(index)}
						onDelete={() => onDeleteEmail(index)}
					/>
				))}

				{isAdding ? (
					<InlineValueEditor
						autoComplete='off'
						autoFocus={true}
						compactLayout={true}
						onCancelButtonClicked={() => setIsAdding(false)}
						onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setEmailValue(ev.target.value)}
						onSaveButtonClicked={onSaveNewEmail}
						onKeyDown={(ev: React.KeyboardEvent<HTMLInputElement>) => {
							if (ev.keyCode === 13) {
								onSaveNewEmail();
							}
						}}
					/>
				) : (
					<div className={css(styleSheet.emailCardAddButton)}>
						<Button kind='link' onClick={() => setIsAdding(true)}>
							+ Add Email Address
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
